import { Pill, thinScrollbar } from '@vgrubs/components';
import styled from 'styled-components';

/**
 * The chat page's own layout: a full-bleed stage holding both panes.
 *
 * There is deliberately no card here any more. The page used to draw a white
 * one and float the inbox, the thread header and the composer on top of it -
 * but a card inside a page is a second frame around content that was already
 * framed, and it cost a 50px gutter on all four sides plus its own border.
 * Dropping it hands that space back to the conversation and leaves the three
 * floating panels reading as controls laid straight on the app's own surface.
 *
 * What used to be white behind the messages is now the page's cream, which is
 * why the thread's fades are told about it - see `--chat-fade`.
 */

/**
 * The stage is exactly one screen tall, minus the gap it keeps above and below.
 *
 * This page carries no `AdminHeader`: the whole screen is the conversation, and
 * a title bar above it only ate 75px to repeat a word the sidebar already
 * highlights.
 *
 * `--app-vh` is the VISIBLE height, published by `useVisualViewportHeight` -
 * see that file for why neither `vh` nor `dvh` is enough once a phone keyboard
 * is involved. `100dvh` is the fallback where the API is missing.
 *
 * Keep `GAP` in step with `PageTopGap` below and with the negative bottom
 * margin: too tall and the document outgrows the viewport, so the window
 * scrolls behind a conversation that is already scrolling itself.
 */
const VIEWPORT = 'var(--app-vh, 100dvh)';
export const GAP = 16;
/**
 * Phones have no gap of their own: the stage runs edge to edge and the only
 * inset is the one the floating pieces already pad themselves with.
 *
 * Anything here is added ON TOP of that padding, and only above and below - the
 * sides come from the panels alone. At 12 the thread sat 24px off the top and
 * bottom of the screen against 12 at the sides, which on a phone reads as the
 * conversation being pushed away from the edges it is supposed to fill.
 */
export const GAP_SM = 0;

/**
 * `ContentStyled`'s own padding, which this page cancels to run to the edges.
 * It is shared with every other screen, so it is clawed back here rather than
 * made conditional there.
 */
const CONTENT_PADDING = 50;
const CONTENT_PADDING_SM = 16;
const CONTENT_PADDING_BOTTOM_SM = 40;

/**
 * What is left of ContentStyled's side gutter after the bleed - so the panels
 * sit this far plus their own INSET off the window, rather than hard against
 * it. Flush to the glass reads as clipped, not as roomy.
 *
 * Phones keep the full bleed: there the gutter is 16px to begin with and the
 * conversation needs the width more than it needs the air.
 */
const SIDE_GUTTER = 16;

const COLUMN_HEIGHT = `calc(${VIEWPORT} - ${GAP * 2}px)`;
const COLUMN_HEIGHT_SM = `calc(${VIEWPORT} - ${GAP_SM * 2}px)`;
/**
 * `AdminHeader`'s own height on a phone, plus its rule. The stage gives it back
 * when the page renders one over the list - the whole point of the arithmetic
 * above is that the page is exactly one screen, and a bar added on top of a
 * full-height stage is how the window ends up scrolling behind a list that is
 * already scrolling itself.
 */
const MOBILE_HEADER = 61;

/** The inset every floating piece shares, and the inbox's own width. */
const INSET = 16;
const LIST_WIDTH = 300;

/** The stage's top gutter, as a spacer rather than a margin that collapses. */
export const PageTopGap = styled.div`
  margin-top: ${GAP}px;

  @media (max-width: 800px) {
    margin-top: ${GAP_SM}px;
  }
`;

export const ChatCard = styled.div<{ $withHeader?: boolean }>`
  position: relative;
  height: ${COLUMN_HEIGHT};
  /* This page owns the surface the conversation scrolls over, so it is the one
     that tells the thread's header and composer what to fade into. Without it
     they ramp to white and leave a band across the cream. */
  --chat-fade: ${({ theme }) => theme.background};
  --chat-fade-end: ${({ theme }) => theme.background + '00'};
  /*
   * Deliberately NOT clipped. The three panels laid on this stage each carry a
   * 30px-blur shadow, and clipping here cut every one of them off along the
   * stage's rim - a seam you could trace around the whole page once the white
   * card stopped hiding it. Nothing needs the clip: the conversation is
   * bounded by its own scroll area, not by this box.
   */
  overflow: visible;
  /* The sides give back all but SIDE_GUTTER of ContentStyled's padding; the
     bottom cancels its own and returns only the gap this page wants. */
  margin: 0 -${CONTENT_PADDING - SIDE_GUTTER}px -${CONTENT_PADDING - GAP}px;

  @media (max-width: 800px) {
    height: ${({ $withHeader }) =>
      $withHeader
        ? `calc(${COLUMN_HEIGHT_SM} - ${MOBILE_HEADER}px)`
        : COLUMN_HEIGHT_SM};
    margin: 0 -${CONTENT_PADDING_SM}px -${CONTENT_PADDING_BOTTOM_SM - GAP_SM}px;
  }
`;

/**
 * The inbox column: search and filter on top, the list panel below, both inset
 * from the card's corner exactly as the thread header is from its own.
 *
 * Below 1200px it stops floating and fills the card, since only one of the two
 * panes is mounted at a time down there.
 */
export const InboxColumn = styled.div<{ $hiddenOnMobile?: boolean }>`
  position: absolute;
  left: ${INSET}px;
  top: ${INSET}px;
  bottom: ${INSET}px;
  width: ${LIST_WIDTH}px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  /*
   * The same distance the column keeps from the walls, so the three stacked
   * pieces are spaced by one rule rather than two: 8px between them against 16
   * around them made the tabs read as stuck to the list rather than as their
   * own control above and below it.
   */
  gap: ${INSET}px;

  @media (max-width: 1200px) {
    right: ${INSET}px;
    width: auto;
    display: ${({ $hiddenOnMobile }) => ($hiddenOnMobile ? 'none' : 'flex')};
  }

  /*
   * Out to the edges on a phone, where the panel under this has no card of its
   * own any more (see InboxPanel below). An inset only reads as a margin while
   * something is drawn on it - without the white sheet it is the rows that
   * would look indented, and they already carry their own 12px.
   */
  @media (max-width: 800px) {
    left: 0;
    right: 0;
    top: 12px;
    bottom: 12px;
    /* Follows the inset down, keeping the one rule above true. */
    gap: 12px;
  }
`;

/**
 * The search, pinned inside the panel above the rows it acts on.
 *
 * It used to float above the panel as its own row. Moving it in is what makes
 * the column read as two things - the inbox and the thread - rather than a
 * stack of bars where some belong to the box below and some do not.
 *
 * Not sticky: the panel is bounded and only the rows under this scroll, so it
 * stays put without leaving the flow.
 */
export const InboxControls = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  /* The 12 the whole stack is spaced by - the tabs, this and the chips under
     it are three rows of one control each, and their own top padding is what
     sets them apart. */
  padding: 12px 12px 0;
`;

/**
 * The filter chips, on the line under the search.
 *
 * Scrolls sideways rather than wrapping: a second line of chips would push the
 * first row of the list down the moment the column narrows, and these are a
 * strip of options - the shape a phone already reads as swipeable.
 */
export const FilterRow = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  /* Same 12 as the row above - see InboxControls. */
  padding: 12px 12px 0;
  overflow-x: auto;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * One filter, on or off.
 *
 * Selected is the filled brand pill; off is the same shape drawn in outline, so
 * turning one on is a fill rather than a new object appearing. Both keep the
 * chip's own size - a filter that changed width when toggled would shuffle the
 * ones beside it.
 */
export const FilterChip = styled(Pill).attrs({
  $height: '28px',
  $minWidth: 'auto',
  $minWidthSM: 'auto',
  $padding: '4px 10px',
})<{ $selected?: boolean }>`
  flex: none;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.ease200ms};
  background: ${({ theme, $selected }) =>
    $selected ? theme.primary : theme.white};
  color: ${({ theme, $selected }) =>
    $selected ? theme.white : theme.darkGray};
  border: ${({ theme, $selected }) =>
    $selected ? '1px solid transparent' : theme.borders.border1};

  :hover {
    background: ${({ theme, $selected }) =>
      $selected ? theme.primary : theme.gray};
  }
`;

/**
 * The list's own panel.
 *
 * Deliberately NOT clipped: the clip that matters - keeping rows off the
 * rounded bottom corners - lives on the scroll area itself, which is the
 * element with the corners to protect. One here would cut off anything a row
 * ever needs to open downward over the list.
 */
export const InboxPanel = styled.div`
  /* Anchors the floating "+". */
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  border-radius: 20px;

  /*
   * No card on a phone.
   *
   * The card earns its edges on a desktop, where it is one of two panes side by
   * side and has to say where the inbox stops and the conversation starts. On a
   * phone the list IS the screen - there is nothing beside it to be separated
   * from - and a white sheet with a border drawn a few pixels inside the
   * viewport just frames the whole page in itself.
   */
  @media (max-width: 800px) {
    background: transparent;
    border: none;
    box-shadow: none;
    border-radius: 0;
  }
`;

/**
 * Merchants / Sales / Internal Team, at the top of the list's own card.
 *
 * Inside the card rather than floating above it: the two halves and the list
 * they switch between are one object, and a pair of pills on the cream above
 * the panel read as page furniture belonging to neither.
 */
/**
 * The list's heading, between the filter chips and the first row.
 *
 * Tight to the rows underneath and loose from the chips above, so it reads as
 * the top of the list rather than as a fourth control.
 */
export const InboxTitle = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  padding: 12px 12px 0px;
`;

export const ScopeTabs = styled.div<{ $wrap?: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;

  /*
   * The bottom 4px is the keyboard focus ring's room, handed straight back as
   * a negative margin so the list below does not move. Chrome draws that ring a
   * few pixels OUTSIDE the tab, and this row clips anything outside itself -
   * see the overflow note below - so with the 0 this once had, tabbing to a tab
   * drew a ring sliced flat along its bottom edge. The 12s on the other three
   * sides were already wide enough to hold it.
   */
  padding: 12px 12px 4px;
  margin-bottom: -4px;
  /*
   * The row gives way before a label does - see ScopeTab.
   *
   * Note this makes the row a scroll container on BOTH axes: a scroller that is
   * auto on one axis computes the other to auto as well, which is why anything
   * hanging outside the tabs - the focus ring here, the unread disc below -
   * needs padding to live in rather than just an offset.
   */
  overflow-x: auto;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }

  /*
   * No space of its own on a phone: the column above already stands 12px off
   * the page header, and a second 12 here stacked into 24 between the two.
   * One rule owns that distance, and this is the one that gives way.
   *
   * Given up as a negative margin rather than as padding, because the row is a
   * scroll container: a horizontal overflow makes the vertical one scroll too,
   * and the unread disc hangs 6px above the tabs, so a row with no padding
   * clips it flat along its top edge. The padding is the disc's room and the
   * margin takes the same amount back off the layout.
   */
  @media (max-width: 800px) {
    gap: 12px;
    padding-top: 8px;
    margin-top: -8px;
  }

  /*
   * Four tabs do not fit a 300px column at any readable size, so with the
   * Dispatcher tab they become two even rows of two - a grid rather than a
   * wrap, so every tab is the same width and none is left stretched alone on
   * a line. The 8px row gap is the column gap, and leaves room for the unread
   * disc hanging off a second-row tab.
   */
  ${({ $wrap }) =>
    $wrap &&
    `
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    @media (max-width: 800px) {
      gap: 8px;
    }
  `}
`;

/**
 * Filled red when selected, rather than the app's usual white-on-grey tab.
 *
 * The white-selected tab is right on the cream page, where white IS the raised
 * state; on this card white is the surface it sits on, so the selected half had
 * nothing to distinguish it but a shadow. Red states it outright, and it is the
 * same red the selected row in the list underneath already uses.
 */
export const ScopeTab = styled.button<{ $selected?: boolean }>`
  /*
   * The three of them fill the row, but they divide it by label rather than
   * into equal thirds: an even split in a 300px column left "Internal Team"
   * clipped mid-word, and a tab nobody can read is worse than an uneven row.
   * Each starts at its own width and takes an equal share of what is left over,
   * so the row ends flush with the card either way.
   */
  flex: 1 1 auto;
  min-width: 0;
  height: 38px;
  padding: 0 10px;
  border-radius: 200px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  /* Anchor for the unread disc hung off the corner. */
  position: relative;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  background: ${({ theme, $selected }) =>
    $selected ? theme.primary : 'transparent'};
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.primary : theme.lightGray)};
  color: ${({ theme, $selected }) =>
    $selected ? theme.white : theme.darkGray};

  :hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme, $selected }) =>
      $selected ? theme.white : theme.primary};
  }
`;

/**
 * Unread, per half of the inbox - which half is worth looking at.
 *
 * Hung off the corner the way the sidebar hangs its count off a collapsed
 * icon, rather than sitting inside the pill: in the row the label owns, a
 * number beside it competed with the word, and at this column width "Client
 * Support" and a count could not share one line without wrapping.
 *
 * One colour in both states. The disc lands mostly on the card, which is white
 * whichever tab is selected; the ring is what keeps it separate where it laps
 * onto the selected tab, which is the same red.
 */
export const ScopeTabCount = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  box-shadow: 0 0 0 2px ${({ theme }) => theme.white};
`;

/**
 * The search takes the slack; the filter button beside it keeps its 40px.
 *
 * 40 here too, rather than the 42 the shared composer surface comes out at.
 * That 42 is right where the field is one of three full-width pills (the
 * thread's title bar, the tabs, the composer) and they have to agree; in this
 * row its neighbour is a 40px disc, and a field two pixels taller than the
 * button next to it is the sort of thing you see without being able to name.
 *
 * Scoped to this slot on purpose: `SearchBar` is shared with the member pickers
 * in the modals, where there is no disc to match and nothing to fix.
 */
export const SearchSlot = styled.div`
  flex: 1;
  min-width: 0;

  > * {
    height: 40px;
  }

  /* The two pixels come off the field's own padding rather than being taken by
     force, so the height above is what the content wants anyway and nothing
     has to overflow to fit it. */
  input {
    padding: 5px 0;
  }
`;

/**
 * The "+" that starts a conversation, floating in the list's bottom corner.
 *
 * It used to sit in the row with search and filter, wearing that row's white
 * disc so the three matched. In the corner it is on its own, and matching
 * nothing: a white disc floating over white rows has no edge to read, and the
 * one action on this screen that MAKES something should not look like the two
 * that narrow what is already there. So it is the filled primary disc this
 * feature already uses for send and for the merchant's launcher.
 *
 * Inside the panel rather than over the whole column, which keeps it clear of
 * the Active/Archived pair underneath and lets the panel's own clip round it
 * off with everything else.
 */
export const NewButton = styled.button`
  position: absolute;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.primary};
  /* Its own lift, not the panel's hairline: this one is above the rows rather
     than beside them, and needs to say so. */
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  transition: ${({ theme }) => theme.transitions.ease200ms};

  :hover {
    transform: scale(1.05);
  }

  svg {
    display: block;
    width: 18px;
    height: 18px;
  }

  @media (max-width: 800px) {
    right: 12px;
    bottom: 12px;
  }
`;

/**
 * One quiet line, the same treatment the empty thread gets.
 *
 * `NoDataComponent`'s illustration is sized for a full page - in a 300px
 * column it reads as a poster for the absence of conversations.
 */
export const InboxEmpty = styled.div`
  /* Centred in the panel rather than sitting at the top. The 1px is the paging
     sentinel below it, which stays mounted so the observer keeps its element -
     without allowing for it the panel would scroll by exactly that much. */
  height: calc(100% - 1px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px 16px;
`;

/**
 * The scroll container; the panel around it is bounded at every width.
 *
 * It carries the panel's bottom corners now that the panel itself does not
 * clip - a selected row is a filled red box, and at the very end of the list it
 * would otherwise square off a corner the panel has rounded.
 */
export const InboxList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  border-radius: 0 0 20px 20px;

  ${thinScrollbar}

  @media (max-width: 800px) {
    border-radius: 0 0 16px 16px;
  }
`;

/**
 * A control in the thread's title bar: back, details, archive.
 *
 * Each is an icon with a label beside it, and the label hides below 800px - so
 * on a phone the glyph stops being an ornament next to the word and becomes the
 * entire control, at which point the 16px it ships at is both hard to read and
 * hard to hit. It grows there instead, into a target a thumb can find.
 */
export const HeaderActionSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  @media (max-width: 800px) {
    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

/**
 * The conversation starts where the inbox ends, so nothing scrolls underneath
 * it. Its own header and composer add the matching inset from there.
 */
export const ThreadArea = styled.div<{ $hiddenOnMobile?: boolean }>`
  height: 100%;
  margin-left: ${INSET + LIST_WIDTH}px;

  @media (max-width: 1200px) {
    margin-left: 0;
    display: ${({ $hiddenOnMobile }) => ($hiddenOnMobile ? 'none' : 'block')};
  }
`;
